import type { ChromaKeyConfig } from './types';

interface ResolvedConfig {
  keyR: number;
  keyG: number;
  keyB: number;
  tolerance: number;
  feather: number;
  shrink: number;
  softness: number;
}

const DEFAULT_CONFIG: ResolvedConfig = {
  keyR: 0,
  keyG: 255,
  keyB: 0,
  tolerance: 35,
  feather: 20,
  shrink: 2,
  softness: 3,
};

// ============================================================
// GLSL Shaders
// ============================================================

const VERT_QUAD = `#version 300 es
in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

// Pass 1: video texture sampled with Y-flip to correct WebGL orientation
const FRAG_CHROMA_KEY = `#version 300 es
precision highp float;
in vec2 vUV;
uniform sampler2D uVideo;
uniform vec3 uKeyColor;
uniform float uTolerance;
uniform float uFeather;
out vec4 fragColor;

void main() {
  vec2 videoUV = vec2(vUV.x, 1.0 - vUV.y);
  vec4 px = texture(uVideo, videoUV);
  vec3 rgb = px.rgb;

  float keyR = uKeyColor.r, keyG = uKeyColor.g, keyB = uKeyColor.b;

  float cb  = -0.169 * rgb.r - 0.331 * rgb.g + 0.5   * rgb.b;
  float cr  =  0.5   * rgb.r - 0.419 * rgb.g - 0.081  * rgb.b;
  float kCb = -0.169 * keyR  - 0.331 * keyG  + 0.5    * keyB;
  float kCr =  0.5   * keyR  - 0.419 * keyG  - 0.081  * keyB;

  float dist = length(vec2(cb - kCb, cr - kCr));

  float alpha;
  if (dist <= uTolerance) {
    alpha = 0.0;
  } else if (dist <= uTolerance + uFeather) {
    float t = (dist - uTolerance) / uFeather;
    alpha = t * t * (3.0 - 2.0 * t);
  } else {
    alpha = 1.0;
  }

  fragColor = vec4(rgb, alpha);
}`;

// Pass 2: erosion reads from FBO — no Y-flip needed
const FRAG_ERODE = `#version 300 es
precision highp float;
in vec2 vUV;
uniform sampler2D uTex;
uniform vec2 uTexelSize;
out vec4 fragColor;

void main() {
  vec4 c = texture(uTex, vUV);
  float a = c.a;
  a = min(a, texture(uTex, vUV + vec2(-uTexelSize.x, 0.0)).a);
  a = min(a, texture(uTex, vUV + vec2( uTexelSize.x, 0.0)).a);
  a = min(a, texture(uTex, vUV + vec2(0.0, -uTexelSize.y)).a);
  a = min(a, texture(uTex, vUV + vec2(0.0,  uTexelSize.y)).a);
  a = min(a, texture(uTex, vUV + vec2(-uTexelSize.x, -uTexelSize.y)).a);
  a = min(a, texture(uTex, vUV + vec2( uTexelSize.x, -uTexelSize.y)).a);
  a = min(a, texture(uTex, vUV + vec2(-uTexelSize.x,  uTexelSize.y)).a);
  a = min(a, texture(uTex, vUV + vec2( uTexelSize.x,  uTexelSize.y)).a);
  fragColor = vec4(c.rgb, a);
}`;

// Pass 3: blur reads from FBO — no Y-flip needed
const FRAG_BLUR = `#version 300 es
precision highp float;
in vec2 vUV;
uniform sampler2D uTex;
uniform vec2 uDirection;
uniform float uKernel[17];
uniform int uRadius;
out vec4 fragColor;

void main() {
  vec4 c = texture(uTex, vUV);
  float sum = c.a * uKernel[0];
  float wSum = uKernel[0];
  for (int i = 1; i <= 16; i++) {
    if (i > uRadius) break;
    float w = uKernel[i];
    float a1 = texture(uTex, vUV + uDirection * float(i)).a;
    float a2 = texture(uTex, vUV - uDirection * float(i)).a;
    sum += (a1 + a2) * w;
    wSum += w * 2.0;
  }
  fragColor = vec4(c.rgb, sum / wSum);
}`;

// Pass 4: composite — video texture with Y-flip, alpha FBO without flip
const FRAG_COMPOSITE = `#version 300 es
precision highp float;
in vec2 vUV;
uniform sampler2D uVideo;
uniform sampler2D uAlpha;
uniform vec3 uKeyColor;
out vec4 fragColor;

void main() {
  vec2 videoUV = vec2(vUV.x, 1.0 - vUV.y);
  vec4 px = texture(uVideo, videoUV);
  float alpha = texture(uAlpha, vUV).a;

  if (alpha <= 0.0) {
    fragColor = vec4(0.0);
    return;
  }

  vec3 rgb = px.rgb;
  float keyG = uKeyColor.g;
  float keyR = uKeyColor.r;
  float keyB = uKeyColor.b;

  bool isGreen = keyG > keyR && keyG > keyB;
  bool isBlue  = keyB > keyR && keyB > keyG;

  // Despill: only apply to semi-transparent edge pixels, scale by (1-alpha)
  // so fully opaque foreground is untouched
  float spillStrength = 1.0 - alpha;
  if (isGreen) {
    float cap = max(rgb.r, rgb.b);
    if (rgb.g > cap) rgb.g = mix(rgb.g, cap, spillStrength);
  } else if (isBlue) {
    float cap = max(rgb.r, rgb.g);
    if (rgb.b > cap) rgb.b = mix(rgb.b, cap, spillStrength);
  } else {
    float cap = max(rgb.g, rgb.b);
    if (rgb.r > cap) rgb.r = mix(rgb.r, cap, spillStrength);
  }

  fragColor = vec4(rgb * alpha, alpha);
}`;

// ============================================================
// WebGL helpers
// ============================================================

function compileShader(gl: WebGL2RenderingContext, type: number, src: string): WebGLShader {
  const s = gl.createShader(type)!;
  gl.shaderSource(s, src);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
    const info = gl.getShaderInfoLog(s);
    gl.deleteShader(s);
    throw new Error('Shader compile error: ' + info);
  }
  return s;
}

function linkProgram(gl: WebGL2RenderingContext, vs: string, fs: string): WebGLProgram {
  const v = compileShader(gl, gl.VERTEX_SHADER, vs);
  const f = compileShader(gl, gl.FRAGMENT_SHADER, fs);
  const p = gl.createProgram()!;
  gl.attachShader(p, v);
  gl.attachShader(p, f);
  gl.linkProgram(p);
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
    const info = gl.getProgramInfoLog(p);
    gl.deleteProgram(p);
    throw new Error('Program link error: ' + info);
  }
  gl.deleteShader(v);
  gl.deleteShader(f);
  return p;
}

function createFBO(gl: WebGL2RenderingContext, w: number, h: number) {
  const tex = gl.createTexture()!;
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA16F, w, h, 0, gl.RGBA, gl.HALF_FLOAT, null);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  const fb = gl.createFramebuffer()!;
  gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  return { tex, fb, w, h };
}

function gaussianKernel(radius: number): Float32Array {
  const k = new Float32Array(17);
  const sigma = Math.max(radius * 0.5, 0.5);
  let sum = 0;
  for (let i = 0; i <= radius && i < 17; i++) {
    k[i] = Math.exp(-(i * i) / (2 * sigma * sigma));
    sum += i === 0 ? k[i] : k[i] * 2;
  }
  for (let i = 0; i <= radius && i < 17; i++) k[i] /= sum;
  return k;
}

// ============================================================
// Renderer
// ============================================================

export class ChromaKeyRenderer {
  private _canvas: HTMLCanvasElement;
  private _source: HTMLVideoElement | null = null;
  private _rafId: number | null = null;
  private _running = false;
  private _config: ResolvedConfig;

  // WebGL state (null if fallback to Canvas 2D)
  private _gl: WebGL2RenderingContext | null = null;
  private _progChroma: WebGLProgram | null = null;
  private _progErode: WebGLProgram | null = null;
  private _progBlur: WebGLProgram | null = null;
  private _progComposite: WebGLProgram | null = null;
  private _videoTex: WebGLTexture | null = null;
  private _quadVAO: WebGLVertexArrayObject | null = null;
  private _fbos: ReturnType<typeof createFBO>[] = [];
  private _fboW = 0;
  private _fboH = 0;

  // Canvas 2D fallback state
  private _ctx2d: CanvasRenderingContext2D | null = null;
  private _keyCb = 0;
  private _keyCr = 0;

  constructor(config?: ChromaKeyConfig) {
    this._config = { ...DEFAULT_CONFIG };
    if (config) this._applyConfig(config);

    this._canvas = document.createElement('canvas');

    const gl = this._canvas.getContext('webgl2', {
      premultipliedAlpha: true,
      alpha: true,
      antialias: false,
      depth: false,
      stencil: false,
    }) as WebGL2RenderingContext | null;

    if (gl && gl.getExtension('EXT_color_buffer_half_float')) {
      this._initWebGL(gl);
      console.log('[ChromaKey] WebGL2 GPU pipeline active | renderer:', gl.getParameter(gl.RENDERER));
    } else {
      this._initCanvas2D();
      console.log('[ChromaKey] Canvas 2D fallback (WebGL2 not available)');
    }
  }

  // ============================================================
  // Public API (unchanged)
  // ============================================================

  attachSource(video: HTMLVideoElement): void {
    this._source = video;
  }

  getCanvas(): HTMLCanvasElement {
    return this._canvas;
  }

  start(): void {
    if (this._running) return;
    this._running = true;
    this._tick();
  }

  stop(): void {
    this._running = false;
    if (this._rafId !== null) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
  }

  updateConfig(config: Partial<ChromaKeyConfig>): void {
    this._applyConfig(config);
    if (!this._gl) this._updateKeyChroma();
  }

  destroy(): void {
    this.stop();
    this._source = null;
    if (this._gl) {
      const gl = this._gl;
      this._fbos.forEach(f => { gl.deleteTexture(f.tex); gl.deleteFramebuffer(f.fb); });
      this._fbos = [];
      if (this._videoTex) gl.deleteTexture(this._videoTex);
      if (this._progChroma) gl.deleteProgram(this._progChroma);
      if (this._progErode) gl.deleteProgram(this._progErode);
      if (this._progBlur) gl.deleteProgram(this._progBlur);
      if (this._progComposite) gl.deleteProgram(this._progComposite);
      if (this._quadVAO) gl.deleteVertexArray(this._quadVAO);
    }
    this._canvas.width = 0;
    this._canvas.height = 0;
  }

  // ============================================================
  // Config
  // ============================================================

  private _applyConfig(cfg: Partial<ChromaKeyConfig>): void {
    if (cfg.keyColor !== undefined) {
      this._config.keyR = cfg.keyColor.r;
      this._config.keyG = cfg.keyColor.g;
      this._config.keyB = cfg.keyColor.b;
    }
    if (cfg.tolerance !== undefined) this._config.tolerance = cfg.tolerance;
    if (cfg.feather !== undefined) this._config.feather = cfg.feather;
    if (cfg.shrink !== undefined) this._config.shrink = cfg.shrink;
    if (cfg.softness !== undefined) this._config.softness = cfg.softness;
  }

  // ============================================================
  // WebGL2 backend
  // ============================================================

  private _initWebGL(gl: WebGL2RenderingContext): void {
    this._gl = gl;

    this._progChroma = linkProgram(gl, VERT_QUAD, FRAG_CHROMA_KEY);
    this._progErode = linkProgram(gl, VERT_QUAD, FRAG_ERODE);
    this._progBlur = linkProgram(gl, VERT_QUAD, FRAG_BLUR);
    this._progComposite = linkProgram(gl, VERT_QUAD, FRAG_COMPOSITE);

    this._videoTex = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, this._videoTex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    const vao = gl.createVertexArray()!;
    gl.bindVertexArray(vao);
    const buf = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    for (const prog of [this._progChroma, this._progErode, this._progBlur, this._progComposite]) {
      const loc = gl.getAttribLocation(prog, 'aPos');
      if (loc >= 0) {
        gl.enableVertexAttribArray(loc);
        gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
      }
    }
    gl.bindVertexArray(null);
    this._quadVAO = vao;
  }

  private _ensureFBOs(w: number, h: number): void {
    if (this._fboW === w && this._fboH === h) return;
    const gl = this._gl!;
    this._fbos.forEach(f => { gl.deleteTexture(f.tex); gl.deleteFramebuffer(f.fb); });
    this._fbos = [createFBO(gl, w, h), createFBO(gl, w, h), createFBO(gl, w, h)];
    this._fboW = w;
    this._fboH = h;
  }

  private _renderWebGL(): void {
    const video = this._source;
    if (!video || video.readyState < 2) return;

    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (vw === 0 || vh === 0) return;

    if (this._canvas.width !== vw || this._canvas.height !== vh) {
      this._canvas.width = vw;
      this._canvas.height = vh;
    }

    const gl = this._gl!;
    this._ensureFBOs(vw, vh);

    // Upload video frame (no UNPACK_FLIP_Y — flip handled in shaders)
    gl.bindTexture(gl.TEXTURE_2D, this._videoTex!);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, video);

    gl.bindVertexArray(this._quadVAO);
    gl.disable(gl.BLEND);

    const { keyR, keyG, keyB, tolerance, feather, shrink, softness } = this._config;
    const kR = keyR / 255, kG = keyG / 255, kB = keyB / 255;
    const tolNorm = tolerance / 255;
    const feathNorm = Math.max(1, feather) / 255;
    const texelX = 1 / vw, texelY = 1 / vh;

    // Pass 1: Chroma key → FBO[0] (shader flips video UV.y)
    gl.bindFramebuffer(gl.FRAMEBUFFER, this._fbos[0].fb);
    gl.viewport(0, 0, vw, vh);
    gl.useProgram(this._progChroma!);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this._videoTex!);
    gl.uniform1i(gl.getUniformLocation(this._progChroma!, 'uVideo'), 0);
    gl.uniform3f(gl.getUniformLocation(this._progChroma!, 'uKeyColor'), kR, kG, kB);
    gl.uniform1f(gl.getUniformLocation(this._progChroma!, 'uTolerance'), tolNorm);
    gl.uniform1f(gl.getUniformLocation(this._progChroma!, 'uFeather'), feathNorm);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

    // Pass 2: Erosion (ping-pong FBO[0] ↔ FBO[1])
    let readIdx = 0;
    let writeIdx = 1;
    for (let i = 0; i < shrink; i++) {
      gl.bindFramebuffer(gl.FRAMEBUFFER, this._fbos[writeIdx].fb);
      gl.viewport(0, 0, vw, vh);
      gl.useProgram(this._progErode!);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, this._fbos[readIdx].tex);
      gl.uniform1i(gl.getUniformLocation(this._progErode!, 'uTex'), 0);
      gl.uniform2f(gl.getUniformLocation(this._progErode!, 'uTexelSize'), texelX, texelY);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      const tmp = readIdx; readIdx = writeIdx; writeIdx = tmp;
    }

    // Pass 3: Gaussian blur on alpha (separable H+V)
    const blurRadius = Math.min(Math.max(Math.round(softness * 4), 0), 16);
    if (blurRadius > 0) {
      const kernel = gaussianKernel(blurRadius);

      // Horizontal blur
      gl.bindFramebuffer(gl.FRAMEBUFFER, this._fbos[writeIdx].fb);
      gl.viewport(0, 0, vw, vh);
      gl.useProgram(this._progBlur!);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, this._fbos[readIdx].tex);
      gl.uniform1i(gl.getUniformLocation(this._progBlur!, 'uTex'), 0);
      gl.uniform2f(gl.getUniformLocation(this._progBlur!, 'uDirection'), texelX, 0);
      gl.uniform1fv(gl.getUniformLocation(this._progBlur!, 'uKernel[0]'), kernel);
      gl.uniform1i(gl.getUniformLocation(this._progBlur!, 'uRadius'), blurRadius);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      { const t = readIdx; readIdx = writeIdx; writeIdx = t; }

      // Vertical blur
      gl.bindFramebuffer(gl.FRAMEBUFFER, this._fbos[writeIdx].fb);
      gl.viewport(0, 0, vw, vh);
      gl.useProgram(this._progBlur!);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, this._fbos[readIdx].tex);
      gl.uniform1i(gl.getUniformLocation(this._progBlur!, 'uTex'), 0);
      gl.uniform2f(gl.getUniformLocation(this._progBlur!, 'uDirection'), 0, texelY);
      gl.uniform1fv(gl.getUniformLocation(this._progBlur!, 'uKernel[0]'), kernel);
      gl.uniform1i(gl.getUniformLocation(this._progBlur!, 'uRadius'), blurRadius);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      { const t = readIdx; readIdx = writeIdx; writeIdx = t; }
    }

    // Pass 4: Composite (shader flips video UV.y, alpha FBO uses standard UV)
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, vw, vh);
    gl.useProgram(this._progComposite!);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this._videoTex!);
    gl.uniform1i(gl.getUniformLocation(this._progComposite!, 'uVideo'), 0);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, this._fbos[readIdx].tex);
    gl.uniform1i(gl.getUniformLocation(this._progComposite!, 'uAlpha'), 1);
    gl.uniform3f(gl.getUniformLocation(this._progComposite!, 'uKeyColor'), kR, kG, kB);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

    gl.bindVertexArray(null);
  }

  // ============================================================
  // Canvas 2D fallback (with box blur on alpha)
  // ============================================================

  private _initCanvas2D(): void {
    const ctx = this._canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) throw new Error('Canvas 2D context not available');
    this._ctx2d = ctx;
    this._updateKeyChroma();
  }

  private _updateKeyChroma(): void {
    const { keyR, keyG, keyB } = this._config;
    this._keyCb = -0.169 * keyR - 0.331 * keyG + 0.5 * keyB;
    this._keyCr = 0.5 * keyR - 0.419 * keyG - 0.081 * keyB;
  }

  private _renderCanvas2D(): void {
    const video = this._source;
    if (!video || video.readyState < 2) return;

    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (vw === 0 || vh === 0) return;

    if (this._canvas.width !== vw || this._canvas.height !== vh) {
      this._canvas.width = vw;
      this._canvas.height = vh;
    }

    const ctx = this._ctx2d!;
    ctx.drawImage(video, 0, 0, vw, vh);
    const imageData = ctx.getImageData(0, 0, vw, vh);
    const data = imageData.data;

    const { keyR: kR, keyG: kG, keyB: kB, tolerance: tol, feather, shrink, softness } = this._config;
    const feath = Math.max(1, feather);
    const keyCb = this._keyCb;
    const keyCr = this._keyCr;
    const isGreenKey = kG > kR && kG > kB;
    const isBlueKey = kB > kR && kB > kG;
    const pixelCount = vw * vh;
    const alphaMap = new Float32Array(pixelCount);

    // Pass 1: CbCr chroma distance
    for (let i = 0; i < pixelCount; i++) {
      const idx = i * 4;
      const r = data[idx], g = data[idx + 1], b = data[idx + 2];
      const cb = -0.169 * r - 0.331 * g + 0.5 * b;
      const cr = 0.5 * r - 0.419 * g - 0.081 * b;
      const dCb = cb - keyCb;
      const dCr = cr - keyCr;
      const chromaDist = Math.sqrt(dCb * dCb + dCr * dCr);
      if (chromaDist <= tol) {
        alphaMap[i] = 0;
      } else if (chromaDist <= tol + feath) {
        const t = (chromaDist - tol) / feath;
        alphaMap[i] = t * t * (3 - 2 * t);
      } else {
        alphaMap[i] = 1;
      }
    }

    // Pass 2: 8-connected erosion
    if (shrink > 0) {
      const tmp = new Float32Array(pixelCount);
      let src = alphaMap;
      let dst = tmp;
      for (let pass = 0; pass < shrink; pass++) {
        for (let y = 0; y < vh; y++) {
          for (let x = 0; x < vw; x++) {
            const ci = y * vw + x;
            let minA = src[ci];
            if (x > 0) minA = Math.min(minA, src[ci - 1]);
            if (x < vw - 1) minA = Math.min(minA, src[ci + 1]);
            if (y > 0) {
              minA = Math.min(minA, src[ci - vw]);
              if (x > 0) minA = Math.min(minA, src[ci - vw - 1]);
              if (x < vw - 1) minA = Math.min(minA, src[ci - vw + 1]);
            }
            if (y < vh - 1) {
              minA = Math.min(minA, src[ci + vw]);
              if (x > 0) minA = Math.min(minA, src[ci + vw - 1]);
              if (x < vw - 1) minA = Math.min(minA, src[ci + vw + 1]);
            }
            dst[ci] = minA;
          }
        }
        const swap = src; src = dst; dst = swap;
      }
      if (src !== alphaMap) alphaMap.set(src);
    }

    // Pass 2.5: Box blur on alpha (3 passes approximates Gaussian)
    const blurRadius = Math.min(Math.max(Math.round(softness * 4), 0), 16);
    if (blurRadius > 0) {
      const tmp = new Float32Array(pixelCount);
      for (let iter = 0; iter < 3; iter++) {
        // Horizontal
        for (let y = 0; y < vh; y++) {
          let sum = 0;
          const row = y * vw;
          for (let x = 0; x < blurRadius && x < vw; x++) sum += alphaMap[row + x];
          for (let x = 0; x < vw; x++) {
            const right = x + blurRadius;
            if (right < vw) sum += alphaMap[row + right];
            const left = x - blurRadius - 1;
            if (left >= 0) sum -= alphaMap[row + left];
            const count = Math.min(right, vw - 1) - Math.max(left + 1, 0) + 1;
            tmp[row + x] = sum / count;
          }
        }
        // Vertical
        for (let x = 0; x < vw; x++) {
          let sum = 0;
          for (let y = 0; y < blurRadius && y < vh; y++) sum += tmp[y * vw + x];
          for (let y = 0; y < vh; y++) {
            const bottom = y + blurRadius;
            if (bottom < vh) sum += tmp[bottom * vw + x];
            const top = y - blurRadius - 1;
            if (top >= 0) sum -= tmp[top * vw + x];
            const count = Math.min(bottom, vh - 1) - Math.max(top + 1, 0) + 1;
            alphaMap[y * vw + x] = sum / count;
          }
        }
      }
    }

    // Pass 3: apply alpha, despill (no aggressive color recovery to avoid white halos)
    for (let i = 0; i < pixelCount; i++) {
      const idx = i * 4;
      const alpha = alphaMap[i];
      if (alpha <= 0) { data[idx + 3] = 0; continue; }
      let r = data[idx], g = data[idx + 1], b = data[idx + 2];
      const spill = 1 - alpha;
      if (isGreenKey) {
        const cap = Math.max(r, b);
        if (g > cap) g = Math.round(g + (cap - g) * spill);
      } else if (isBlueKey) {
        const cap = Math.max(r, g);
        if (b > cap) b = Math.round(b + (cap - b) * spill);
      } else {
        const cap = Math.max(g, b);
        if (r > cap) r = Math.round(r + (cap - r) * spill);
      }
      data[idx] = r;
      data[idx + 1] = g;
      data[idx + 2] = b;
      data[idx + 3] = Math.round(alpha * 255);
    }

    ctx.putImageData(imageData, 0, 0);
  }

  // ============================================================
  // Frame loop
  // ============================================================

  private _tick = (): void => {
    if (!this._running) return;
    this._rafId = requestAnimationFrame(this._tick);
    if (this._gl) {
      this._renderWebGL();
    } else {
      this._renderCanvas2D();
    }
  };
}
